import { PROD_URL_HTTPS } from "~/utils/constants";

/**
 * Seção de SEO editorial para páginas 100% curadas pelo CMS (Home,
 * campanhas sazonais) — ao contrário de SeoPLP.tsx, não deriva nada da
 * VTEX. Título/descrição/imagem/JSON-LD são definidos direto no bloco.
 *
 * O bloco do CMS já entrega os campos prontos (title, description, image,
 * jsonLDs) e o framework (`buildPageSeo` em
 * @decocms/start/src/routes/cmsRoute.ts) os lê como estão. O `loader`
 * abaixo só completa os posts do blog (imagem, og:type e autor a partir do
 * JSON-LD BlogPosting).
 */
export interface Props {
  title?: string;
  description?: string;
  /** URL absoluta usada em og:image/twitter:image. */
  image?: string;
  /** og:type — default "website" no `buildHead` do framework quando ausente. */
  type?: string;
  jsonLDs?: unknown[];
}

type JsonLd = Record<string, unknown>;

const AUTHOR = { "@type": "Organization", name: "Daniela Tombini", url: PROD_URL_HTTPS };

function firstImage(image: unknown): string | undefined {
  const first = Array.isArray(image) ? image[0] : image;
  if (typeof first === "string") return first;
  if (first && typeof first === "object" && typeof (first as JsonLd).url === "string") {
    return (first as JsonLd).url as string;
  }
  return undefined;
}

/**
 * Posts do blog: os blocos (scripts/migrate-blog-post.mjs e
 * import-soro-rss.mjs) trazem a imagem e as datas só no JSON-LD
 * BlogPosting. Sem isto, o compartilhamento mostra a imagem padrão do site
 * com og:type "website", e o Google não vê autor no artigo. Outras páginas
 * passam intactas.
 */
export const loader = (props: Props): Props => {
  const index = props.jsonLDs?.findIndex((ld) => (ld as JsonLd)?.["@type"] === "BlogPosting") ?? -1;
  if (index < 0) return props;

  const post = props.jsonLDs![index] as JsonLd;
  const jsonLDs = [...props.jsonLDs!];
  if (!post.author) jsonLDs[index] = { ...post, author: AUTHOR };

  return {
    ...props,
    image: props.image ?? firstImage(post.image),
    type: props.type ?? "article",
    jsonLDs,
  };
};

export const eager = true;
export const sync = true;

export default function SeoPage({ jsonLDs }: Props) {
  if (!jsonLDs?.length) return null;

  return (
    <>
      {jsonLDs.map((jsonLD, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLD) }}
        />
      ))}
    </>
  );
}
